import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/app-layout';
import { WelcomeDialog } from '@/components/welcome-dialog';
import { AppRoutes, ROUTES } from '@/routes';
import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import { DemoSessionProvider, useDemoSession } from '@/providers/DemoSessionProvider';
import { ProjectFormProvider } from '@/providers/ProjectFormProvider';
import { FieldConfigProvider } from '@/contexts/FieldConfigContext';
// TEMPORARY - DEMO ONLY: Developer tools for error demonstration
import { DeveloperToolsDialog } from '@/components/developer-tools-dialog';
import { ErrorDisplay } from '@/components/error';
import { createAppError, type AppError } from '@/types/error.types';
import { useDataMode } from '@/contexts/DataModeContext';
import { DemoLogoutPage } from '@/pages/demo-logout';
import { fetchProjects, fetchEmployees } from '@/lib/data-service';
import type { Project, Employee } from '@/types';
import { formatDate } from '@/lib/utils';

// Helper to convert route path to page name for preferences
const routeToPageName = (pathname: string): string => {
  // Remove leading slash and get first segment
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'dashboard';
  
  // Handle project details route
  if (segments[0] === 'projects' && segments.length > 1) {
    return 'projects';
  }
  
  return segments[0];
};

// Helper to convert page name to route path
const pageNameToRoute = (pageName: string): string => {
  const routeMap: Record<string, string> = {
    'dashboard': ROUTES.DASHBOARD,
    'projects': ROUTES.PROJECTS,
    'portfolio': ROUTES.PORTFOLIO,
    'utilisation': ROUTES.UTILISATION,
    'wip-reporting': ROUTES.WIP_REPORTING,
    'profitability': ROUTES.PROFITABILITY,
    'budgets': ROUTES.BUDGETS,
    'resources': ROUTES.RESOURCES,
    'gantt': ROUTES.GANTT,
    'feature-labs': ROUTES.FEATURE_LABS,
    'settings': ROUTES.SETTINGS,
    'user-preferences': ROUTES.USER_PREFERENCES,
    'category-management': ROUTES.CATEGORY_MANAGEMENT,
    'project-creation-setup': ROUTES.PROJECT_CREATION_SETUP,
    'error-handling-demo': ROUTES.ERROR_HANDLING_DEMO,
    'column-configurator-demo': ROUTES.COLUMN_CONFIGURATOR_DEMO,
    'responsive-table-demo': ROUTES.RESPONSIVE_TABLE_DEMO,
  };
  
  return routeMap[pageName] || ROUTES.DASHBOARD;
};

function AppContent() {
  const { isLoggedIn } = useDemoSession();
  const { preferences, updatePreferences } = useUserPreferences();
  const location = useLocation();
  const navigate = useNavigate();
  // TEMPORARY - DEMO ONLY: Access data mode for developer tools
  const { isDemoMode } = useDataMode();
  
  const [hasInitialized, setHasInitialized] = useState(false);
  const [projectContextData, setProjectContextData] = useState<{
    project: Project;
    projectManager: Employee;
  } | null>(null);
  // TEMPORARY - DEMO ONLY: Developer tools dialog state
  const [showDevTools, setShowDevTools] = useState(false);
  // TEMPORARY - DEMO ONLY: Demo application error state (shown as overlay without crashing the app)
  const [demoAppError, setDemoAppError] = useState<AppError | null>(null);
  
  // Get current page from route
  const currentPage = routeToPageName(location.pathname);
  
  // Initialize and handle initial navigation
  useEffect(() => {
    if (!hasInitialized) {
      setHasInitialized(true);
      
      // Only navigate to preferred landing page if we're on root
      if (location.pathname === '/') {
        const getInitialPage = () => {
          if (preferences.rememberLastPage && preferences.lastVisitedPage) {
            return preferences.lastVisitedPage;
          }
          return preferences.defaultLandingPage;
        };
        
        const initialPage = getInitialPage();
        const initialRoute = pageNameToRoute(initialPage);
        navigate(initialRoute, { replace: true });
      }
    }
  }, [hasInitialized, location.pathname, preferences, navigate]);
  
  // TEMPORARY - DEMO ONLY: Invisible keyboard shortcut for developer tools
  // Only enabled in development mode AND when using demo data
  useEffect(() => {
    const isDevelopment = import.meta.env.DEV;
    
    // Only enable in dev mode AND demo mode
    if (!isDevelopment || !isDemoMode) {
      return;
    }
    
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+Shift+D (or Cmd+Shift+D on Mac)
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        setShowDevTools(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDemoMode]);
  
  // Update last visited page when route changes (but not on settings pages)
  useEffect(() => {
    if (hasInitialized && preferences.rememberLastPage) {
      const page = routeToPageName(location.pathname);
      if (page !== 'user-preferences' && page !== 'settings') {
        updatePreferences({ lastVisitedPage: page });
      }
    }
  }, [location.pathname, preferences.rememberLastPage, hasInitialized, updatePreferences]);

  // Fetch project context data when viewing a project
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const isProjectDetails = pathSegments[0] === 'projects' && pathSegments.length > 1;
    
    if (isProjectDetails) {
      const projectId = pathSegments[1];
      
      const loadProjectContext = async () => {
        try {
          const [projects, employees] = await Promise.all([
            fetchProjects('projects'),
            fetchEmployees('projects'),
          ]);
          
          const project = projects.find((p: Project) => p.id === projectId);
          
          if (project) {
            const projectManager = employees.find((e: Employee) => e.id === project.projectManagerId);
            
            if (projectManager) {
              setProjectContextData({ project, projectManager });
            }
          }
        } catch (error) {
          console.error('Failed to load project context:', error);
          setProjectContextData(null);
        }
      };
      
      loadProjectContext();
    } else {
      setProjectContextData(null);
    }
  }, [location.pathname]);

  // Show logout page if not logged in
  if (!isLoggedIn) {
    return <DemoLogoutPage />;
  }

  // TEMPORARY - DEMO ONLY: Handler for triggering demo application errors
  const handleTriggerDemoError = (error: Error) => {
    const appError = createAppError(error);
    setDemoAppError(appError);
  };

  // TEMPORARY - DEMO ONLY: Handler for closing demo error (X button - returns to drawer)
  const handleCloseDemoError = () => {
    setDemoAppError(null);
    // Keep the drawer open - user returns to the drawer
  };

  // TEMPORARY - DEMO ONLY: Handler for retry/reload actions (closes both error and drawer)
  const handleDismissError = () => {
    setDemoAppError(null);
    setShowDevTools(false); // Close the drawer when using Try Again or Reload
  };

  // Create project context for AppLayout header
  const projectContext = projectContextData ? {
    projectName: projectContextData.project.name,
    clientName: projectContextData.project.clientNames || 'Unknown Client',
    projectManager: projectContextData.projectManager.name,
    startDate: formatDate(projectContextData.project.startDate),
    endDate: formatDate(projectContextData.project.endDate),
    health: projectContextData.project.health,
    status: projectContextData.project.status,
    onBack: () => navigate(ROUTES.PROJECTS),
    onEdit: () => {
      // TODO: Implement edit project functionality
      console.log('Edit project:', projectContextData.project.id);
    },
    onMoreOptions: () => {
      // TODO: Implement more options menu
      console.log('More options for project:', projectContextData.project.id);
    },
  } : undefined;

  return (
    <>
      <WelcomeDialog />
      {/* TEMPORARY - DEMO ONLY: Developer tools dialog for error demonstration */}
      <DeveloperToolsDialog 
        open={showDevTools} 
        onOpenChange={setShowDevTools}
        onTriggerError={handleTriggerDemoError}
        hasActiveError={!!demoAppError}
      />
      {/* Main app content with conditional blur when dev tools is open (and no error) */}
      <div className={showDevTools && !demoAppError ? 'blur-sm pointer-events-none' : ''}>
        <AppLayout 
          currentPage={currentPage}
          projectContext={projectContext}
        >
          <AppRoutes />
        </AppLayout>
      </div>
      {/* TEMPORARY - DEMO ONLY: Demo application error overlay - rendered on top of everything */}
      {demoAppError && (
        <ErrorDisplay
          error={demoAppError}
          recovery={{
            canRetry: true,
            onRetry: handleDismissError, // Try Again closes both error and drawer
            canReload: true,
            onReload: handleDismissError, // Reload closes both error and drawer
          }}
          onClose={handleCloseDemoError} // X button only closes error, keeps drawer open
        />
      )}
    </>
  );
}

export default function App() {
  return (
    <DemoSessionProvider>
      <AppContent />
    </DemoSessionProvider>
  );
}

// Wrapper component that provides context providers inside the router
export function AppWithProviders() {
  return (
    <FieldConfigProvider>
      <ProjectFormProvider>
        <App />
      </ProjectFormProvider>
    </FieldConfigProvider>
  );
}
